/**
 * Display formatting shared by the web app and the AI prompt builder, so a
 * figure reads the same wherever it appears.
 */

export type Currency = 'INR' | 'USD';

export const LAKH = 1_00_000;
export const CRORE = 1_00_00_000;

/**
 * Full-precision currency, grouped for the locale. `en-IN` gives the Indian
 * lakh/crore digit grouping (12,34,567) rather than the western one.
 */
export function formatCurrency(value: number, currency: Currency = 'INR', fractionDigits = 0): string {
  if (!Number.isFinite(value)) return '—';
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * Compact currency. INR uses the Indian scale (L / Cr) because "₹1.2 Cr" is
 * how the number is actually spoken; USD uses K / M / B.
 */
export function formatCompact(value: number, currency: Currency = 'INR'): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (currency === 'INR') {
    if (abs >= CRORE) return `${sign}₹${trim(abs / CRORE)} Cr`;
    if (abs >= LAKH) return `${sign}₹${trim(abs / LAKH)} L`;
    if (abs >= 1_000) return `${sign}₹${trim(abs / 1_000)}K`;
    return `${sign}₹${Math.round(abs)}`;
  }

  if (abs >= 1_000_000_000) return `${sign}$${trim(abs / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${sign}$${trim(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}$${trim(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

function trim(value: number): string {
  // Two decimals below 10, one above — "₹1.25 Cr" but "₹12.5 L".
  const dp = value < 10 ? 2 : 1;
  return value.toFixed(dp).replace(/\.?0+$/, '');
}

export function formatPct(value: number, dp = 1): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(dp)}%`;
}

/**
 * Convert between the two supported display currencies.
 * `usdInr` is the live rate fetched server-side; there is no hardcoded default,
 * because a stale hardcoded rate silently misstates every figure on screen.
 */
export function convert(
  value: number,
  from: Currency,
  to: Currency,
  usdInr: number,
): number {
  if (from === to) return value;
  if (!Number.isFinite(usdInr) || usdInr <= 0) return Number.NaN;
  return from === 'INR' ? value / usdInr : value * usdInr;
}

/** Relative age of a price, for the stale badge. */
export function formatAge(timestampSec: number | null | undefined, nowSec = Date.now() / 1000): string {
  if (timestampSec == null || !Number.isFinite(timestampSec)) return 'never priced';
  const delta = Math.max(0, nowSec - timestampSec);
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
}
